import { Injectable } from '@angular/core';
import { OrderAdDetail } from '@app/core/models/order-ad-detail.model';
import { Order } from '@app/core/models/order.model';
import { AppConfigService } from '@app/core/services/app-config.service';
import { StaticDataCacheService } from './static-data-cache.service';

@Injectable({
	providedIn: 'root'
})
export class StrictPackageGroupsService {

	constructor(
		private _appConfigService: AppConfigService, 
		private _staticDataCacheService: StaticDataCacheService
		) { }

	/**
	 * Detect if 'strict package group' rules are broken. Each package in a strict package group must be 
	 * booked with the same number of issue dates as every package in the group (but can vary by placement and size).
	 * @param order The order to be checked.
	 * @param deletingAdDetails Order lines that are about to be deleted (if applicable), allowing to test whether 
	 * the rule will be broken after deletion.
	 * @returns A string containing the names of packages that currently fail this check (basic whitespace formatted).
	 */
	hasInvalidStrictPackageGroups(order: Order, deletingAdDetails: OrderAdDetail[] | undefined = undefined): string | undefined {
		const strictPackageGroups = this._appConfigService.config.strictPackageGroups;
		if (strictPackageGroups.length === 0) return;
		// If we're deleting order lines, remove them from a copy of the order's order lines to test against.
		const orderAdDetailsToCheck = [...order.orderAdDetails];
		if (deletingAdDetails) {
			for (let deletingAdDetail of deletingAdDetails) {
				orderAdDetailsToCheck.splice(orderAdDetailsToCheck.findIndex(x => x === deletingAdDetail))
			}
		}
		const strictPackages = strictPackageGroups.flat();
		// Count issues per strict package
		let issueCounts = new Map<string, number>();
		for (let adDetail of orderAdDetailsToCheck) {
			if (strictPackages.includes(adDetail.packageId)) {
				issueCounts.set(adDetail.packageId, (issueCounts.get(adDetail.packageId) ?? 0) + 1);
			}
		}
		if (issueCounts.size > 0) {
			// Check if all packages in each strict group have the same issue count
			let failedGroups: string[][] = [];
			for (let group of strictPackageGroups) {
				if (group.length > 1) { // in case of bad config
					const count = issueCounts.get(group[0]) ?? 0;
					for (let i = 1; i < group.length; i++) {
						const packLabels: string[] = [];
						if (count !== (issueCounts.get(group[i]) ?? 0)) {
							for (let packId of group) {
								const pack = this._staticDataCacheService.getPackageById(packId);
								packLabels.push((issueCounts.get(packId) ?? 0) + " x " + (pack?.label ?? packId));
							}
							failedGroups.push(packLabels);
							break;
						}
					}
				}
			}
			return failedGroups.map(x => x.join('\n')).join('\n');
		}
		return;
	}
	
}
